import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeProvider";
import { Moon, Sun, Search, ChevronDown, Globe, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MDX, LANGS } from "./mdx/tokens";

/**
 * NavbarMdx — Melian Dialogue landing v2 header.
 * Strictly excludes the animated status LED badge and Features/BeCo Index/
 * Exchanges/Pricing/Compliance nav links per Directive.
 *
 * Elements: wordmark · Cmd+K trigger · theme toggle · 6-lang selector · trial CTA.
 */
export default function NavbarMdx({ onStartTrial, onOpenCommandBar }) {
    const { theme, toggle } = useTheme();
    const isDark = theme === "dark";
    const [langOpen, setLangOpen] = useState(false);
    const [lang, setLang] = useState("EN");
    const [mobileOpen, setMobileOpen] = useState(false);
    const dropRef = useRef(null);

    useEffect(() => {
        const onDoc = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setLangOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Cmd+K binding — proxies to whatever command bar the app mounts.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                onOpenCommandBar?.();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onOpenCommandBar]);

    const bg = isDark ? "bg-[#090D16]/80" : "bg-white/85";
    const border = isDark ? "border-b border-[#1F2937]" : "border-b border-[#E5E7EB]";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";

    return (
        <header
            className={`sticky top-0 z-50 backdrop-blur-md ${bg} ${border} ${text}`}
            data-testid="navbar-mdx"
        >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-6">
                {/* Wordmark · MD monogram box + full name */}
                <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-wordmark" aria-label="Melian Dialogue home">
                    <span
                        aria-hidden="true"
                        data-testid="navbar-logo-md"
                        className="inline-flex items-center justify-center rounded-md text-white font-inter font-bold tracking-tight"
                        style={{ background: MDX.blueLowSolid, width: 32, height: 32, fontSize: 13, letterSpacing: "0.02em" }}
                    >
                        MD
                    </span>
                    <span className="font-inter font-bold text-lg tracking-tight" style={{ color: MDX.blueLow }}>
                        Melian&nbsp;Dialogue
                    </span>
                </Link>

                {/* Nav links · scroll to sections or route to standalone pages */}
                <nav className="hidden lg:flex items-center gap-5 xl:gap-6 ml-auto" aria-label="Primary" data-testid="navbar-links">
                    {[
                        { label: "How It Works", target: "metrics-matrix"       },
                        { label: "Your Helper",  target: "ai-copilot-comparison" },
                        { label: "Markets",      target: "exchange-footprint"   },
                        { label: "Pricing",      target: "pricing-mdx"          },
                    ].map((l) => (
                        <a
                            key={l.target}
                            href={`#${l.target}`}
                            onClick={(e) => {
                                e.preventDefault();
                                document.querySelector(`[data-testid=${l.target}]`)?.scrollIntoView({ behavior: "smooth" });
                            }}
                            data-testid={`navlink-${l.target}`}
                            className={`font-inter font-semibold text-[13px] transition-opacity ${isDark ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}
                            onMouseEnter={(e) => (e.currentTarget.style.color = MDX.blueLow)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                        >
                            {l.label}
                        </a>
                    ))}

                    {/* Route-based links · About · FAQ · Contact */}
                    {[
                        { label: "About",   to: "/about"   },
                        { label: "FAQ",     to: "/faq"     },
                        { label: "Contact", to: "/contact" },
                    ].map((r) => (
                        <Link
                            key={r.to}
                            to={r.to}
                            data-testid={`navlink-${r.label.toLowerCase()}`}
                            className={`font-inter font-semibold text-[13px] transition-opacity ${isDark ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}
                            onMouseEnter={(e) => (e.currentTarget.style.color = MDX.blueLow)}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                        >
                            {r.label}
                        </Link>
                    ))}

                    <Link
                        to="/login"
                        data-testid="navlink-signin"
                        className={`font-inter font-semibold text-[13px] transition-opacity ${isDark ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}
                        onMouseEnter={(e) => (e.currentTarget.style.color = MDX.blueLow)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                    >
                        Sign In
                    </Link>
                </nav>

                {/* Right controls */}
                <div className="flex items-center gap-2 md:gap-3 shrink-0">
                    {/* Cmd+K command trigger */}
                    <button
                        onClick={() => onOpenCommandBar?.()}
                        aria-label="Open command bar (Cmd+K)"
                        data-testid="navbar-cmdk"
                        className={`hidden md:inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-inter font-medium transition-colors ${
                            isDark
                                ? "border-[#1F2937] hover:border-[#2563EB]/60 text-[#9CA3AF]"
                                : "border-[#E5E7EB] hover:border-[#2563EB]/60 text-[#6B7280]"
                        }`}
                    >
                        <Search className="h-3.5 w-3.5" />
                        <span>Search</span>
                        <kbd className={`ml-2 rounded px-1.5 py-0.5 font-mono text-[10px] ${isDark ? "bg-[#1F2937]" : "bg-[#F3F4F6]"}`}>⌘K</kbd>
                    </button>

                    {/* Theme toggle */}
                    <button
                        onClick={toggle}
                        aria-label="Toggle theme"
                        data-testid="navbar-theme-toggle"
                        className={`p-2 rounded-md border transition-colors ${
                            isDark
                                ? "border-[#1F2937] hover:border-[#2563EB]/60"
                                : "border-[#E5E7EB] hover:border-[#2563EB]/60"
                        }`}
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>

                    {/* 6-language selector */}
                    <div className="relative" ref={dropRef}>
                        <button
                            onClick={() => setLangOpen((s) => !s)}
                            aria-label="Language selector"
                            data-testid="navbar-lang-btn"
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-2 text-xs font-inter font-semibold transition-colors ${
                                isDark
                                    ? "border-[#1F2937] hover:border-[#2563EB]/60"
                                    : "border-[#E5E7EB] hover:border-[#2563EB]/60"
                            }`}
                        >
                            <Globe className="h-3.5 w-3.5" />
                            {lang}
                            <ChevronDown className="h-3 w-3" />
                        </button>
                        {langOpen && (
                            <div
                                data-testid="navbar-lang-menu"
                                className={`absolute right-0 mt-2 w-44 rounded-lg border shadow-xl overflow-hidden ${
                                    isDark ? "bg-[#0F1524] border-[#1F2937]" : "bg-white border-[#E5E7EB]"
                                }`}
                            >
                                {LANGS.map((l) => (
                                    <button
                                        key={l.code}
                                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                                        data-testid={`navbar-lang-${l.code}`}
                                        className={`flex items-center justify-between w-full px-3 py-2 text-sm font-inter transition-colors ${
                                            lang === l.code
                                                ? "bg-[#2563EB]/10 text-[#2563EB] font-semibold"
                                                : isDark ? "hover:bg-[#1F2937]" : "hover:bg-[#F3F4F6]"
                                        }`}
                                    >
                                        <span className="proportional-nums">{l.code}</span>
                                        <span className="text-xs opacity-70">{l.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile hamburger toggle · visible below lg */}
                    <button
                        onClick={() => setMobileOpen((s) => !s)}
                        aria-label={mobileOpen ? "Close menu" : "Open menu"}
                        data-testid="navbar-hamburger"
                        className={`lg:hidden p-2 rounded-md border ${isDark ? "border-[#1F2937]" : "border-[#E5E7EB]"}`}
                    >
                        {mobileOpen ? <X size={16} /> : <Menu size={16} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer · slides in below the navbar */}
            {mobileOpen && (
                <div
                    className={`lg:hidden border-t ${isDark ? "border-[#1F2937] bg-[#0F1524]" : "border-[#E5E7EB] bg-white"}`}
                    data-testid="navbar-mobile-drawer"
                >
                    <nav className="px-4 py-4 flex flex-col gap-1">
                        {[
                            { label: "How It Works", target: "metrics-matrix"       },
                            { label: "Your Helper",  target: "ai-copilot-comparison" },
                            { label: "Markets",      target: "exchange-footprint"   },
                            { label: "Pricing",      target: "pricing-mdx"          },
                        ].map((l) => (
                            <a
                                key={l.target}
                                href={`#${l.target}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setMobileOpen(false);
                                    document.querySelector(`[data-testid=${l.target}]`)?.scrollIntoView({ behavior: "smooth" });
                                }}
                                data-testid={`m-navlink-${l.target}`}
                                className="font-inter font-semibold text-[14px] py-2 px-2 rounded-md hover:opacity-80"
                            >
                                {l.label}
                            </a>
                        ))}
                        {[
                            { label: "About",   to: "/about"   },
                            { label: "FAQ",     to: "/faq"     },
                            { label: "Contact", to: "/contact" },
                            { label: "Sign In", to: "/login"   },
                        ].map((r) => (
                            <Link
                                key={r.to}
                                to={r.to}
                                onClick={() => setMobileOpen(false)}
                                data-testid={`m-navlink-${r.label.toLowerCase()}`}
                                className="font-inter font-semibold text-[14px] py-2 px-2 rounded-md hover:opacity-80"
                            >
                                {r.label}
                            </Link>
                        ))}
                        <button
                            onClick={() => { setMobileOpen(false); onStartTrial?.(); }}
                            data-testid="m-navlink-trial"
                            style={{ background: MDX.greenCtaSolid }}
                            className="mt-2 font-inter font-bold uppercase tracking-wider text-white rounded-md px-4 py-3 text-[13px]"
                        >
                            Start 7-Day Free Trial
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
}
